import { FormikHelpers } from "formik";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { notifications } from "@/app/container/notifications";
import {
  OrderByDirection,
  TradeContractChangeRequestHistoryDocument,
  useCounterTradeContractChangeRequestMutation,
  useCreateTradeContractChangeRequestMutation,
  useUpdateTradeContractChangeRequestMutation,
} from "@/app/types/generated/graphql";

import {
  ForwardTradeChangeRequestModalFormValues,
  ForwardTradeChangeRequestModalMode,
  UseForwardTradeChangeRequestModal,
} from "./forward-trade-change-request-modal.types";

export const useForwardTradeChangeRequestModal: UseForwardTradeChangeRequestModal = ({
  initialData,
  mode,
  closeModal,
}) => {
  const { t } = useTranslation();

  // Ensure variables are set, otherwise TradeContractChangeRequestHistoryDocument
  // will query all previously navigated tradeContractIds
  const refetchTradeContractChangeRequestHistory = useMemo(
    () => ({
      query: TradeContractChangeRequestHistoryDocument,
      variables: {
        input: {
          tradeContractId: initialData.tradeContractId,
          sortOrderInputs: [
            {
              field: "requestedOn",
              sortOrder: OrderByDirection.Desc,
            },
          ],
        },
      },
    }),
    [initialData.tradeContractId],
  );

  const [createTradeContractChangeRequest] = useCreateTradeContractChangeRequestMutation({
    refetchQueries: [refetchTradeContractChangeRequestHistory],
  });

  const [updateTradeContractChangeRequest] = useUpdateTradeContractChangeRequestMutation({
    refetchQueries: [refetchTradeContractChangeRequestHistory],
  });

  const [counterTradeContractChangeRequest] = useCounterTradeContractChangeRequestMutation({
    refetchQueries: [refetchTradeContractChangeRequestHistory],
  });

  const mutation = useMemo(() => {
    const mutationMap = {
      [ForwardTradeChangeRequestModalMode.Add]: createTradeContractChangeRequest,
      [ForwardTradeChangeRequestModalMode.Edit]: updateTradeContractChangeRequest,
      [ForwardTradeChangeRequestModalMode.Counter]: counterTradeContractChangeRequest,
    };

    return mutationMap[mode];
  }, [createTradeContractChangeRequest, updateTradeContractChangeRequest, counterTradeContractChangeRequest, mode]);

  const onSubmit = useCallback(
    async (
      values: ForwardTradeChangeRequestModalFormValues,
      formikHelpers: FormikHelpers<ForwardTradeChangeRequestModalFormValues>,
    ) => {
      try {
        formikHelpers.setSubmitting(true);
        await mutation?.({
          variables: {
            input: {
              tradeContractId: initialData.tradeContractId,
              newDeliveryDate: values.deliveryDate,
              newPaymentDate: values.paymentDate,
            },
          },
        });
        notifications.success({ description: t("Change request submitted successfully") });
        closeModal();
      } catch (e) {
        e instanceof Error &&
          notifications.error({
            description: t(e?.message),
          });
      } finally {
        formikHelpers.setSubmitting(false);
      }
    },
    [mutation, initialData.tradeContractId, t, closeModal],
  );

  return {
    onSubmit,
  };
};
